import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import BottomNavigationBar from './BottomNavigationBar';
import { useAuth } from '../contexts/AuthContext';
import { talentAPI } from '../services/api';
import './SearchResults.css';

const SearchResults = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  
  const [searchQuery, setSearchQuery] = useState('');
  const [searchLocation, setSearchLocation] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const query = searchParams.get('q') || '';
    const category = searchParams.get('category') || '';
    const location = searchParams.get('location') || '';

    setSearchQuery(query);
    setSearchLocation(location);

    if (query || category) {
      performSearch(query, category, location);
    }
  }, [searchParams]);

  const performSearch = async (query, categorySlug, location) => {
    setLoading(true);
    setError(null);

    try {
      // Search for talent profiles
      const searchParams = {};
      
      if (query) {
        searchParams.search = query;
      }
      
      if (location) {
        searchParams.location = location;
      }
      
      // Add pagination
      searchParams.per_page = 50;

      const response = await talentAPI.search(searchParams);
      
      if (response.success) {
        setResults(response.data || []);
      } else {
        setError(response.message || 'Failed to perform search');
        setResults([]);
      }
    } catch (err) {
      setError('Failed to perform search. Please try again.');
      console.error('Search error:', err);
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleLoginClick = () => {
    navigate('/login');
  };

  const handleSignupClick = () => {
    navigate('/signup');
  };

  const handleProfileClick = () => {
    navigate('/profile');
  };

  const handleAccountSettingsClick = () => {
    navigate('/settings');
  };

  const handleCategoriesClick = () => {
    navigate('/categories');
  };

  const handleHowItWorksClick = () => {
    navigate('/#how-it-works');
  };

  const handleForumClick = () => {
    navigate('/forum');
  };

  const handleJobsClick = () => {
    navigate('/vacancies');
  };

  const handleCategoryClick = (category) => {
    navigate(`/categories/${category.slug}`);
  };

  const clearSearch = () => {
    navigate('/');
  };

  const getSearchSummary = () => {
    if (searchQuery && searchLocation) {
      return `Results for "${searchQuery}" in ${searchLocation}`;
    } else if (searchQuery) {
      return `Results for "${searchQuery}"`;
    } else if (selectedCategory) {
      return `Results in ${selectedCategory.name}`;
    }
    return 'Search Results';
  };

  return (
    <div className="App">
      <Header 
        onLoginClick={handleLoginClick}
        onSignupClick={handleSignupClick}
        isLoggedIn={isAuthenticated}
        user={user}
        onHomeClick={() => navigate('/')}
        onCategoriesClick={handleCategoriesClick}
        onHowItWorksClick={handleHowItWorksClick}
        onProfileClick={handleProfileClick}
        onAccountSettingsClick={handleAccountSettingsClick}
        onForumClick={handleForumClick}
        onJobsClick={handleJobsClick}
        currentPage="search"
      />
      
      <div className="search-results-page">
        <div className="container">
          <div className="search-results-header">
            <div className="search-breadcrumb">
              <button onClick={() => navigate('/')} className="breadcrumb-link">
                Home
              </button>
              <span className="breadcrumb-separator">/</span>
              <span className="breadcrumb-current">Search Results</span>
            </div>
            
            <h1 className="search-results-title">{getSearchSummary()}</h1>
            
            <div className="search-filters">
              <div className="search-filter-item">
                <span className="filter-label">Query:</span>
                <span className="filter-value">{searchQuery || 'All'}</span>
              </div>
              {searchLocation && (
                <div className="search-filter-item">
                  <span className="filter-label">Location:</span>
                  <span className="filter-value">{searchLocation}</span>
                </div>
              )}
              <button onClick={clearSearch} className="clear-search-btn">
                Clear Search
              </button>
            </div>
          </div>

          <div className="search-results-content">
            {loading ? (
              <div className="loading-state">
                <div className="loading-spinner"></div>
                <p>Searching for results...</p>
              </div>
            ) : error ? (
              <div className="error-state">
                <div className="error-icon">
                  <i className="bi bi-exclamation-triangle"></i>
                </div>
                <h3>Search Error</h3>
                <p>{error}</p>
                <button onClick={() => window.location.reload()} className="retry-btn">
                  Try Again
                </button>
              </div>
            ) : results.length === 0 ? (
              <div className="no-results-state">
                <div className="no-results-icon">
                  <i className="bi bi-search"></i>
                </div>
                <h3>No Results Found</h3>
                <p>We couldn't find any results matching your search criteria.</p>
                <div className="no-results-suggestions">
                  <h4>Try:</h4>
                  <ul>
                    <li>Using different keywords</li>
                    <li>Checking your spelling</li>
                    <li>Using more general terms</li>
                    <li>Browsing our categories</li>
                  </ul>
                </div>
                <button onClick={() => navigate('/categories')} className="browse-categories-btn">
                  Browse Categories
                </button>
              </div>
            ) : (
              <div className="search-results-grid">
                <div className="results-summary">
                  <p>Found {results.length} result{results.length !== 1 ? 's' : ''}</p>
                </div>
                
                <div className="results-list talent-results-list">
                  {results.map((profile) => (
                    <div key={profile.id} className="search-result-card talent-profile-card">
                      <div className="result-card-content">
                        <div className="talent-profile-avatar">
                          {profile.user?.profile_photo_url ? (
                            <img src={profile.user.profile_photo_url} alt={profile.user.name} />
                          ) : (
                            <div className="talent-avatar-placeholder">
                              {profile.user?.name?.charAt(0)?.toUpperCase() || 'T'}
                            </div>
                          )}
                        </div>
                        <div className="result-details">
                          <h3 className="result-title">
                            {profile.user?.name || 'Talent'}
                          </h3>
                          {profile.professional_title && (
                            <p className="talent-professional-title">{profile.professional_title}</p>
                          )}
                          {profile.bio && (
                            <p className="result-description">
                              {profile.bio.length > 150 
                                ? `${profile.bio.substring(0, 150)}...` 
                                : profile.bio}
                            </p>
                          )}
                          <div className="result-meta">
                            {profile.location && (
                              <span className="talent-location">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                                  <circle cx="12" cy="10" r="3"></circle>
                                </svg>
                                {profile.location}
                              </span>
                            )}
                            {profile.work_type && (
                              <span className="talent-work-type">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect>
                                  <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
                                </svg>
                                {profile.work_type}
                              </span>
                            )}
                          </div>
                          {profile.core_skills && profile.core_skills.length > 0 && (
                            <div className="talent-skills">
                              {profile.core_skills.slice(0, 5).map((skill, index) => (
                                <span key={index} className="talent-skill-tag">{skill}</span>
                              ))}
                              {profile.core_skills.length > 5 && (
                                <span className="talent-skill-more">+{profile.core_skills.length - 5} more</span>
                              )}
                            </div>
                          )}
                        </div>
                        <div className="result-actions">
                          <button 
                            onClick={() => {
                              // Navigate to talent profile using username or profile_slug
                              const identifier = profile.user?.username || profile.profile_slug;
                              if (identifier) {
                                navigate(`/talent/${identifier}`);
                              }
                            }}
                            className="view-profile-btn"
                          >
                            View Profile
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <line x1="5" y1="12" x2="19" y2="12"></line>
                              <polyline points="12,5 19,12 12,19"></polyline>
                            </svg>
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      
      <Footer />
      {isAuthenticated && (
        <BottomNavigationBar
          onHomeClick={() => navigate('/')}
          onCategoriesClick={handleCategoriesClick}
          onHowItWorksClick={handleHowItWorksClick}
          onProfileClick={handleProfileClick}
          onAccountSettingsClick={handleAccountSettingsClick}
          onForumClick={handleForumClick}
          onJobsClick={handleJobsClick}
          isLoggedIn={isAuthenticated}
          user={user}
        />
      )}
    </div>
  );
};

export default SearchResults;
